import {
    MailListRowDataType,
    MailRowDataPropertyGetter,
    TableView,
    isItemOfMessageType,
} from 'owa-mail-list-store';
import { TombstoneReasonType } from 'owa-mail-list-tombstone';
import ConversationType from 'owa-service/lib/contract/ConversationType';
import Item from 'owa-service/lib/contract/Item';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import Message from 'owa-service/lib/contract/Message';

const arrayEquals = require('array-equal');

/**
 * Property sync map that stores the tombstone reason type to sync functions
 */
let clientToServerPropertySyncMap = new Map<
    TombstoneReasonType,
    (serverRow: MailListRowDataType, tableView: TableView) => boolean
>();

clientToServerPropertySyncMap[TombstoneReasonType.Pin] = syncClientPinPropertyToServerRow;
clientToServerPropertySyncMap[TombstoneReasonType.Category] = syncClientCategoryPropertyToServerRow;
clientToServerPropertySyncMap[TombstoneReasonType.Read] = syncClientReadPropertyToServerRow;

/**
 * Syncs the client (local) PIN property by stamping it on the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @returns flag indicating whether the pin operation property value was already in sync
 */
function syncClientPinPropertyToServerRow(
    serverRow: MailListRowDataType,
    tableView: TableView
): boolean {
    let serverPinPropertyValue;
    const isConversationView = tableView.tableQuery.listViewType == ReactListViewType.Conversation;
    if (isConversationView) {
        serverPinPropertyValue = (serverRow as ConversationType).LastDeliveryOrRenewTime;
    } else {
        serverPinPropertyValue = (serverRow as Item).ReceivedOrRenewTime;
    }

    const clientPinPropertyValue = MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(
        serverRow.InstanceKey,
        tableView
    );

    const isPinPropertyInSync =
        new Date(serverPinPropertyValue).toString() === new Date(clientPinPropertyValue).toString();

    if (!isPinPropertyInSync) {
        if (isConversationView) {
            (serverRow as ConversationType).LastDeliveryOrRenewTime = clientPinPropertyValue;
        } else {
            (serverRow as Item).ReceivedOrRenewTime = clientPinPropertyValue;
        }
    }

    return isPinPropertyInSync;
}

/**
 * Syncs the client (local) CATEGORY property by stamping it on the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @returns flag indicating whether the category property value was already in sync
 */
function syncClientCategoryPropertyToServerRow(
    serverRow: MailListRowDataType,
    tableView: TableView
): boolean {
    const clientCategories = MailRowDataPropertyGetter.getCategories(
        serverRow.InstanceKey,
        tableView
    );
    const clientCategoriesSorted = clientCategories ? clientCategories.slice().sort() : [];
    const serverCategoriesSorted = serverRow.Categories ? serverRow.Categories.slice().sort() : [];
    const isCategoryPropertyInSync = arrayEquals(clientCategoriesSorted, serverCategoriesSorted);

    if (!isCategoryPropertyInSync) {
        serverRow.Categories = clientCategories;
    }

    return isCategoryPropertyInSync;
}

/**
 * Syncs the client (local) READ property by stamping it on the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @returns flag indicating whether the read property value was already in sync
 */
function syncClientReadPropertyToServerRow(
    serverRow: MailListRowDataType,
    tableView: TableView
): boolean {
    let payloadRow;
    let payloadUnreadCount;
    let isReadPropertyInSync;

    const clientUnreadCount = MailRowDataPropertyGetter.getUnreadCount(
        serverRow.InstanceKey,
        tableView
    );
    const isConversationView = tableView.tableQuery.listViewType == ReactListViewType.Conversation;

    if (isConversationView) {
        payloadRow = serverRow as ConversationType;
        payloadUnreadCount = payloadRow.UnreadCount;
        isReadPropertyInSync = clientUnreadCount == payloadUnreadCount;
        if (!isReadPropertyInSync) {
            payloadRow.UnreadCount = clientUnreadCount;
        }
    } else {
        // For non-message types we shall not sync anything
        if (isItemOfMessageType(serverRow)) {
            payloadRow = serverRow as Message;
            payloadUnreadCount = payloadRow.IsRead ? 0 : 1;
            isReadPropertyInSync = clientUnreadCount == payloadUnreadCount;

            if (!isReadPropertyInSync) {
                payloadRow.IsRead = clientUnreadCount == 0;
            }
        } else {
            isReadPropertyInSync = true;
        }
    }

    return isReadPropertyInSync;
}

/**
 * Syncs client triage property for the given triage operation in the server row payload
 * @param serverRow row payload from server
 * @param tableView tableView to which the row belongs
 * @param tombstoneReason type of tombstone reason used to determine which property to sync
 * @returns flag indicating whether the triage property for given tombstone reason is in sync
 */
export default function syncClientPropertyToServerRow(
    serverRow: MailListRowDataType,
    tableView: TableView,
    tombstoneReason: TombstoneReasonType
): boolean {
    const syncFunctionForTombstoneReason = clientToServerPropertySyncMap[tombstoneReason];
    return syncFunctionForTombstoneReason(serverRow, tableView);
}
